import { useQueryClient } from '@tanstack/react-query'
import {
  addDays,
  endOfDay,
  format,
  isEqual,
  isSameWeek,
  parseISO,
  startOfDay,
  startOfWeek,
} from 'date-fns'
import PropTypes from 'prop-types'
import React from 'react'
import { useUserRole } from '@opallabs/api'
import { MessageKeys, useDebouncedValue, useWorkspace } from '@opallabs/hooks'
import { queryKeys } from '@opallabs/in-market-preview'
import { kuiperClassNames, Loader } from '@opallabs/kuiper'
import { toast } from '@opallabs/lapidary'
import { getTrackableComponent } from '@opallabs/quanta'
import { ContentCreatedToast } from '../Toasts'
import ActiveModal from './ActiveModal'
import { buildCategoriesForSort } from './buildCategoriesForSort'
import CardContext from './cardContext'
import { CategoryRows } from './CategoryRows'
import { WEEK_VIEW_SCROLL_OFFSET } from './constants'
import { addContentToList, reorderContentInList } from './contentManager'
import { DayHeaders } from './DayHeaders'
import ErrorBoundary from './ErrorBoundary'
import ErrorDisplay from './ErrorBoundary/ErrorDisplay'
import {
  buildContextMenuCallbacks,
  multiSorter,
  updatePendingContent,
} from './helpers'
import modals from './modals'
import { useCreateContent } from './queries/useCreateContent'
import { useWorkspaceServiceIconMap } from './queries/useSystemServices'
import { CONTENT_ID_CHUNK_SIZE, CONTENT_PAGE_LIMIT } from './queryHelpers'
import { actionTypes, reducer } from './reducer'
import './styles.scss'
import { WeekViewMode, WeekViewSortType } from './types'
import { ContentMapProvider, MomentMapProvider } from './useMomentMap'
import { useWeekHotKeys } from './useWeekHotKeys'
import { useWeekViewSortOptions } from './useWeekViewSortOptions'
import { WeekViewGlobalContext } from './weekViewGlobalContext'
import { useWeekViewSettings } from './WeekViewSettingsContext'

// Content deletion is async (the server acks with a 202 before the row is
// actually gone) and a Relay/websocket broadcast for the same post can arrive
// after the optimistic removal, re-deserializing it back into the Redux
// content slice. Content mode renders from that slice, so the card reappears.
// We tombstone deleted ids for this window and drop them from the content fed
// to the map; tombstones self-expire so a genuinely failed delete re-surfaces.
const DELETED_CONTENT_GRACE_MS = 30_000

// Load a week only after navigation pauses this long. Clicking or pressing
// through several weeks quickly then loads just the week the user stops on,
// not every week passed on the way.
const WEEK_CHANGE_FETCH_DELAY_MS = 250

// How many requests for content by id are in flight at once. A busy week in a
// large workspace lists a few thousand ids.
const CONTENT_ID_MAX_CONCURRENCY = 5

const WeekView = ({
  activeFilters,
  selectedFilters,
  addViewToStore,
  content,
  filteredContent,
  createMoment,
  currentBrandId,
  currentError,
  currentWeekChannels,
  currentWeekLabelSets,
  deleteContent,
  deleteMoment,
  errors,
  focusedDay,
  getContent,
  getContentById,
  getLabelSets,
  getMoments,
  handleError,
  isShroudVisible,
  joinBrand,
  labelSets,
  launchMomentDuplicator,
  loadUpdatedJsonApiEntities,
  momentContentIds,
  moments,
  navigateTo,
  onAddToBoard,
  onAddToPlan,
  onDayChange,
  onDismissError,
  preConstraint,
  removeJsonApiEntityRelationship,
  removeViewFromStore,
  requestPrivacyGroupAccess,
  searchTerm,
  setSearchTerm,
  story,
  updateContent,
  theme = 'light',
  trackers = {},
  updateMoment,
  uploadAsset,
  weekStartsOn,
}) => {
  const { data: userRoleData } = useUserRole()
  const queryClient = useQueryClient()
  const { uuid: workspace_id } = useWorkspace()
  const [momentsAreScrolled, setMomentsAreScrolled] = React.useState(false)
  const [activeColumn, setActiveColumn] = React.useState({
    index: -1,
    category: null,
  })
  const [disableActiveColumn, setDisableActiveColumn] = React.useState(false)
  const {
    collapseContent,
    coverImageDisplay,
    selectedSort,
    setValidSorts,
    spanInMarketDays,
    viewMode,
    workflowOnMomentsEnabled,
  } = useWeekViewSettings()

  // Placing this at the top of week view to ensure it starts
  // fetching as soon as possible
  useWorkspaceServiceIconMap(currentBrandId)

  const { sortOptions } = useWeekViewSortOptions()

  const viewInterval = {
    start: startOfDay(focusedDay),
    end: endOfDay(addDays(focusedDay, 6)),
  }

  React.useEffect(
    () => setValidSorts(sortOptions),
    [sortOptions, setValidSorts]
  )

  const prevStoryColorRef = React.useRef(story?.color)

  const [placeholderMoment, setPlaceholderMoment] = React.useState(null)
  // Content id -> tombstone expiry (see DELETED_CONTENT_GRACE_MS).
  const deletedContentIdsRef = React.useRef(new Map())

  const [isUpdatingContent, setIsUpdatingContent] = React.useState(false)
  const [contentLoadsInFlight, setContentLoadsInFlight] = React.useState(0)
  const [isDraggingContent, setIsDraggingContent] = React.useState(false)

  const [state, dispatch] = React.useReducer(reducer, {
    activeEntityInModal: null,
    activeModalKey: null,
    destinationMoment: null,
    destinationMomentPivotName: null,
    duplicatedContentSource: null,
    dropData: null,
    height: 0,
    isLoadingLabelSets: true,
    isLoadingMoments: false,
    isMomentCreatorActive: false,
    momentOffsetData: {},
    moments: null,
    // The week and search the moments on hand were loaded for.
    momentsLoadedFor: null,
    momentUpdates: [],
    originMoment: null,
    originMomentPivotName: null,
    searchTerm,
    startDate: startOfWeek(focusedDay, { weekStartsOn }),
  })

  const startMomentFetch = () =>
    dispatch({ type: actionTypes.START_MOMENT_FETCH })

  const weekViewRef = React.useRef(true)

  React.useEffect(() => {
    const conditionsToPreventBootstrap = [
      isUpdatingContent,
      state.momentUpdates.length,
      state.isMomentCreatorActive,
      state.isLoadingLabelSets,
      state.isLoadingMoments,
    ]

    if (conditionsToPreventBootstrap.some((condition) => condition)) return

    dispatch({
      type: actionTypes.BOOTSTRAP_WEEK_VIEW,
      data: {
        content,
        labelSets,
        moments,
        weekStartsOn,
        story,
        workflowOnMomentsEnabled,
      },
    })
  }, [
    content,
    labelSets,
    moments,
    weekStartsOn,
    story,
    workflowOnMomentsEnabled,
    state.momentUpdates.length,
    state.isLoadingLabelSets,
    state.isLoadingMoments,
    isUpdatingContent,
    state.isMomentCreatorActive,
  ])

  React.useEffect(() => {
    addViewToStore()

    getLabelSets()
      .then(() => dispatch({ type: actionTypes.END_LOADING_LABEL_SETS }))
      .catch((err) => {
        throw err
      })

    trackers?.track?.('load', {
      inStory: !!story,
      isExpanded: !collapseContent,
      storyId: story && story.id,
    })

    return () => removeViewFromStore()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMoments = React.useCallback(
    ({ showLoader = true } = {}) => {
      getMoments({
        focusedDay: state.startDate,
        searchString: searchTerm,
        currentBrandId,
        startMomentFetch: showLoader ? startMomentFetch : () => undefined,
        endMomentFetch: () =>
          dispatch({
            type: actionTypes.END_MOMENT_FETCH,
            data: { startDate: state.startDate, searchTerm },
          }),
      })
    },
    [getMoments, searchTerm, state.startDate, currentBrandId]
  )

  // Brings in content created inline: creating content leaves the moment's
  // `updated_at` unchanged, so `loadUpdatedJsonApiEntities` keeps the stored
  // moment and its old content list (#16447). No loader, so the week stays
  // mounted. Reads the latest `fetchMoments`, and none once unmounted, because
  // a create can finish after the week or the view changes. Refetching the
  // old week would cancel the current fetch (`takeLatest`) and leave its
  // loader up.
  const latestFetchMoments = React.useRef(null)
  React.useLayoutEffect(() => {
    latestFetchMoments.current = fetchMoments
    return () => {
      latestFetchMoments.current = null
    }
  }, [fetchMoments])
  const refetchMoments = React.useCallback(
    () => latestFetchMoments.current?.({ showLoader: false }),
    []
  )

  // Gem and the monolith's own modals post this after they change a moment;
  // refetch so the change shows without a reload.
  React.useEffect(() => {
    const handleRefresh = (event) => {
      if (event.data?.message === MessageKeys.refreshMoments) fetchMoments()
    }
    window.addEventListener('message', handleRefresh)
    return () => window.removeEventListener('message', handleRefresh)
  }, [fetchMoments])

  // The ids already asked for, and the week, search and asset setting they
  // were asked for under. Any of those changing starts the list again.
  const requestedContentIds = React.useRef({ key: null, ids: new Set() })

  // Bumped on each new content fetch so an in-flight fetch abandons itself
  // when the week changes instead of paging a stale week.
  const contentFetchGeneration = React.useRef(0)

  // Fetch the week's content one page at a time, continuing only while a page
  // comes back full. Don't request the next page speculatively: the server
  // repeats the full content query and permission check for every page, and
  // nearly every week fits in the first page, so a speculative second request
  // is almost always wasted database work.
  const fetchAllContent = React.useCallback(async () => {
    if (!state.startDate) return
    // Card preview images — and thus asset references — render only when
    // In-Market Duration is off, so only fetch them then.
    const includeAssets = !spanInMarketDays
    const generation = (contentFetchGeneration.current += 1)
    // The first page can replace the week's content with a search's, so the
    // cards ask for theirs again when they are next shown.
    requestedContentIds.current = { key: null, ids: new Set() }
    let offset = 0
    let lastPageFull = true
    setContentLoadsInFlight((count) => count + 1)
    try {
      while (lastPageFull) {
        let pageSize
        try {
          pageSize = await getContent(
            offset,
            state.startDate,
            currentBrandId,
            searchTerm,
            includeAssets
          )
        } catch {
          return
        }
        // A newer fetch started (week changed) — stop paging this stale one.
        if (generation !== contentFetchGeneration.current) return
        lastPageFull = pageSize === CONTENT_PAGE_LIMIT
        offset += CONTENT_PAGE_LIMIT
      }
    } finally {
      setContentLoadsInFlight((count) => count - 1)
    }
  }, [
    getContent,
    state.startDate,
    currentBrandId,
    searchTerm,
    spanInMarketDays,
  ])

  const fetchStartDate = useDebouncedValue(
    state.startDate,
    WEEK_CHANGE_FETCH_DELAY_MS
  )

  // Show the loader during the delay so the previous week's moments aren't
  // rendered under the new week's dates.
  React.useEffect(() => {
    if (!state.startDate) return
    startMomentFetch()
  }, [state.startDate])

  React.useEffect(() => {
    if (!fetchStartDate) return
    fetchMoments()
  }, [fetchStartDate]) // eslint-disable-line react-hooks/exhaustive-deps

  // Content mode shows the content dated in the week, which is what the date
  // query finds. Separate from the moment fetch: content re-fetches when the
  // week changes OR when In-Market Duration toggles (to add/drop asset
  // references), while moments — unaffected by duration — don't.
  React.useEffect(() => {
    if (!fetchStartDate || viewMode !== WeekViewMode.Content) return
    fetchAllContent()
  }, [fetchStartDate, spanInMarketDays, viewMode]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContentByIds = React.useCallback(
    async (ids) => {
      const includeAssets = !spanInMarketDays
      const chunks = []
      for (let i = 0; i < ids.length; i += CONTENT_ID_CHUNK_SIZE) {
        chunks.push(ids.slice(i, i + CONTENT_ID_CHUNK_SIZE))
      }
      // Replaced when the week, search or date query changes, so the rest of
      // these chunks are no longer wanted.
      const requested = requestedContentIds.current
      let next = 0
      const loadChunks = async () => {
        while (
          next < chunks.length &&
          requested === requestedContentIds.current
        ) {
          const chunk = chunks[next]
          next += 1
          try {
            await getContentById(chunk, currentBrandId, includeAssets)
          } catch {
            // Forgotten, so the next change to the moments asks again.
            chunk.forEach((id) => requested.ids.delete(id))
          }
        }
      }
      setContentLoadsInFlight((count) => count + 1)
      try {
        await Promise.all(
          Array.from(
            { length: Math.min(CONTENT_ID_MAX_CONCURRENCY, chunks.length) },
            loadChunks
          )
        )
      } finally {
        setContentLoadsInFlight((count) => count - 1)
      }
    },
    [currentBrandId, getContentById, spanInMarketDays]
  )

  // Moments mode draws content only on moment cards, and a moment lists its
  // content wherever that content is dated. The date query misses whatever
  // falls outside the week, so the cards ask for their content by id. A
  // layout effect, so a card never paints a moment as having no content in
  // the moment between the moments arriving and their content being asked for.
  React.useLayoutEffect(() => {
    const loadedFor = state.momentsLoadedFor
    const hasMomentsForView =
      !state.isLoadingMoments &&
      loadedFor !== null &&
      loadedFor.searchTerm === searchTerm &&
      isEqual(loadedFor.startDate, state.startDate)
    if (viewMode !== WeekViewMode.Moment || !hasMomentsForView) return
    const key = [state.startDate?.toISOString(), searchTerm, spanInMarketDays]
      .map(String)
      .join('|')
    if (requestedContentIds.current.key !== key) {
      requestedContentIds.current = { key, ids: new Set() }
    }
    const requested = requestedContentIds.current.ids
    const ids = (momentContentIds ?? []).filter((id) => !requested.has(id))
    if (ids.length === 0) return
    ids.forEach((id) => requested.add(id))
    fetchContentByIds(ids)
  }, [
    fetchContentByIds,
    momentContentIds,
    searchTerm,
    spanInMarketDays,
    state.isLoadingMoments,
    state.momentsLoadedFor,
    state.startDate,
    viewMode,
  ])

  React.useEffect(() => {
    if (isSameWeek(focusedDay, state.startDate)) return
    dispatch({
      type: actionTypes.CHANGE_WEEK,
      data: { focusedDay, weekStartsOn },
    })
  }, [focusedDay]) // eslint-disable-line react-hooks/exhaustive-deps

  const onMoveContent = React.useCallback(
    ({
      originMomentId,
      destinationMomentId,
      contentId,
      newContentPosition,
    }) => {
      const originMoment = state.moments[originMomentId]
      const destinationMoment = state.moments[destinationMomentId]

      const originMomentContent = originMoment?.posts || []
      const destinationMomentContent = destinationMoment?.posts || []

      const movedContent = {
        ...content[contentId],
        moment: { id: destinationMomentId },
      }

      dispatch({
        type: actionTypes.MOVE_CONTENT,
        data: {
          originMomentId,
          originMomentContent,
          destinationMomentId,
          destinationMomentContent,
          movedContent,
          newContentPosition,
        },
      })

      setIsUpdatingContent(true)
      setDisableActiveColumn(false)
      setIsDraggingContent(false)

      const updatedContentList = addContentToList(
        newContentPosition,
        movedContent,
        destinationMomentContent
      )

      updatePendingContent(updatedContentList, originMoment, updateContent)
        // Remove the post relationship from the origin moment in redux
        .then(() =>
          removeJsonApiEntityRelationship({
            primaryResourceType: 'moment',
            primaryResourceId: originMomentId,
            relationshipType: 'posts',
            relationshipId: contentId,
          })
        )
        .then(() => setIsUpdatingContent(false))
        .catch((err) => {
          console.error(
            `Unable to move content to moment ${destinationMoment.id}.`
          )
          setIsUpdatingContent(false)
          throw err
        })
    },
    [content, state.moments, updateContent, removeJsonApiEntityRelationship]
  )

  const onReorderContent = React.useCallback(
    ({ destinationIndex, moment, originIndex }) => {
      dispatch({
        type: actionTypes.REORDER_CONTENT,
        data: {
          destinationIndex,
          moment,
          originIndex,
        },
      })

      setIsUpdatingContent(true)
      setDisableActiveColumn(false)
      setIsDraggingContent(false)

      const updatedContentList = reorderContentInList(
        moment.posts,
        originIndex,
        destinationIndex
      )

      updatePendingContent(updatedContentList, moment, updateContent)
        .then(() => setIsUpdatingContent(false))
        .catch((err) => {
          console.error(`Unable to reorder content for moment ${moment.id}.`)
          setIsUpdatingContent(false)
          throw err
        })
    },
    [updateContent]
  )

  const setDragOrigin = React.useCallback((data) => {
    dispatch({ type: actionTypes.SET_DRAG_ORIGIN, data })
  }, [])

  const createContent = useCreateContent()

  React.useEffect(() => {
    if (!state.duplicatedContentSource) return
    const destinationMomentId = state.destinationMoment.id
    const destinationMoment = state.moments[destinationMomentId]
    const optimisticUuid = state.optimisticDuplicateUuid
    const duplicatedContent = destinationMoment.posts.find(
      (post) => post.uuid === optimisticUuid
    )
    const sourcePost = state.duplicatedContentSource
    const serviceId = sourcePost.service?.id
    // Moment mode's source post carries `moment` at the top level;
    // Content mode's carries the moment id via `relationships.moment.id`.
    // Fall back to the destination moment if neither is present (shouldn't
    // happen in practice, but avoids a null `duplicated_from_moment` ref).
    const sourceMomentId =
      sourcePost.moment?.id ??
      sourcePost.relationships?.moment?.id ??
      destinationMomentId
    const settle = () =>
      dispatch({
        type: actionTypes.DUPLICATE_CONTENT_SETTLED,
        data: { momentId: destinationMomentId, optimisticUuid },
      })
    if (!serviceId) {
      console.error('DUPLICATE_CONTENT: source post is missing serviceId', {
        sourcePostId: sourcePost.id,
      })
      toast('Duplicate failed — missing channel.', { type: 'error' })
      settle()
      return
    }
    // Duplicate via the same V2 content.create endpoint the inline creator
    // uses, passing the PostDuplicator params the server branches on. The
    // JSON:API response is deserialized into Redux by
    // `loadUpdatedJsonApiEntities`, and `refetchMoments` adds the new post to
    // the moment's content list.
    // `mutateAsync`, not `mutate`: a per-call `onSuccess` is dropped when
    // another create on this mutation starts first.
    createContent
      .mutateAsync({
        serviceId,
        momentId: destinationMomentId,
        duplicatedFromMomentId: sourceMomentId,
        duplicatedFromPostId: sourcePost.id,
        position: duplicatedContent?.position,
      })
      .then(
        (data) => {
          invalidateInMarketQuery(queryClient, workspace_id)
          if (typeof loadUpdatedJsonApiEntities === 'function') {
            loadUpdatedJsonApiEntities(data)
          }
          settle()
          refetchMoments()
        },
        (error) => {
          console.error('Failed to duplicate content', {
            error,
            sourcePostId: sourcePost.id,
            destinationMomentId,
          })
          settle()
          toast(
            'Duplicate failed — the content could not be copied. Please try again.',
            { type: 'error' }
          )
        }
      )
  }, [state.duplicatedContentSource]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (weekViewRef.current) {
      weekViewRef.current = false
      return
    }

    // Set the store search term before fetching so the wormhole reducer's
    // content reset (keyed on the store term) fires on the fetch's first page.
    setSearchTerm(searchTerm)
    fetchMoments()
    if (viewMode === WeekViewMode.Content) fetchAllContent()
  }, [searchTerm]) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (story?.color && story.color !== prevStoryColorRef.current) {
      prevStoryColorRef.current = story.color
      fetchMoments()
    }
  }, [story?.color, fetchMoments])

  const activateModalState = React.useCallback(
    ({ activeModalKey, entityType, params }) => {
      dispatch({
        type: actionTypes.ACTIVATE_MODAL,
        data: { activeModalKey, entityType, params },
      })
    },
    []
  )

  const changeWeek = React.useCallback(
    (firstDayOfWeek) => {
      dispatch({
        type: actionTypes.CHANGE_WEEK,
        data: { focusedDay: firstDayOfWeek, weekStartsOn },
      })
      onDayChange(firstDayOfWeek)
    },
    [onDayChange, weekStartsOn]
  )

  // The control bar that used to hold these keys now sits above the week, in a
  // tree of its own. The modal state that silences them is still in here.
  useWeekHotKeys({
    focusedDay: state.startDate,
    isShroudVisible: isShroudVisible || !!state.activeEntityInModal,
    onWeekChange: changeWeek,
  })

  const deactivateModalState = React.useCallback(() => {
    dispatch({ type: actionTypes.DEACTIVATE_MODAL })
  }, [])

  const destroyContent = React.useCallback(
    ({ id, moment }) => {
      dispatch({
        type: actionTypes.DELETE_CONTENT,
        data: {
          momentId: moment.id,
          preConstraint,
          collapseContent,
          coverImageDisplay,
          selectedSort,
          update: {
            postCount: moment.postCount - 1,
            posts: moment.posts.filter((post) => post.id !== id),
          },
        },
      })
      deleteContent(id, moment.id)
      deletedContentIdsRef.current.set(
        String(id),
        Date.now() + DELETED_CONTENT_GRACE_MS
      )
      invalidateInMarketQuery(queryClient, workspace_id)
    },
    [
      preConstraint,
      collapseContent,
      coverImageDisplay,
      selectedSort,
      deleteContent,
      queryClient,
      workspace_id,
    ]
  )

  // Drop tombstoned (recently deleted) posts a stale push may have re-added to
  // the content slice. Recomputes whenever the slice changes — including the
  // re-add — so the deleted card stays gone. Expired tombstones fall through.
  const visibleContent = React.useMemo(() => {
    const source = filteredContent ?? content
    const tombstones = deletedContentIdsRef.current
    if (!source || tombstones.size === 0) return source
    const now = Date.now()
    const next = {}
    let removedAny = false
    for (const [contentId, item] of Object.entries(source)) {
      const expiresAt = tombstones.get(contentId)
      if (expiresAt != null && expiresAt > now) {
        removedAny = true
        continue
      }
      if (expiresAt != null) tombstones.delete(contentId)
      next[contentId] = item
    }
    return removedAny ? next : source
  }, [filteredContent, content])

  const destroyMoment = React.useCallback(
    ({ id }) => {
      dispatch({
        type: actionTypes.DELETE_MOMENT,
        data: {
          momentId: id,
        },
      })
      deleteMoment(id)
    },
    [deleteMoment]
  )

  const duplicateContent = React.useCallback(
    (contentItem, moment, pivotName) => {
      const contentList = [...moment.posts]
      contentList.sort(multiSorter([{ sortBy: 'position', order: 'ASC' }]))
      dispatch({
        type: actionTypes.DUPLICATE_CONTENT,
        data: { contentItem, contentList, moment, pivotName },
      })
      invalidateInMarketQuery(queryClient, workspace_id)
    },
    [queryClient, workspace_id]
  )

  const onCreateMoment = React.useCallback(
    async (moment, selectedServices, options = {}) => {
      const newMoment = await createMoment({ moment, story })
      // `mutateAsync`, not `mutate`: a per-call `onSuccess` fires only for the
      // latest call, so with several channels the earlier creates would never
      // settle.
      const contentCreates = (selectedServices ?? []).map((service) =>
        createContent
          .mutateAsync({
            serviceId: service.id,
            momentId: newMoment.payload.data.id,
            ...(options.applyNameToContent &&
              moment.title && { name: moment.title }),
          })
          .then((data) => {
            invalidateInMarketQuery(queryClient, workspace_id)
            toast(<ContentCreatedToast selectedServices={selectedServices} />, {
              type: 'success',
              toastId: 'success-content-added-to-moment',
              closeOnClick: true,
            })
            /** Deserialize content response into redux. */
            if (typeof loadUpdatedJsonApiEntities === 'function') {
              loadUpdatedJsonApiEntities(data)
            }

            // Content mode's Time sort anchors the post to a specific hour.
            // The moment stays day-level (it can't carry a time), so patch
            // the content's schedule after creation. Best-effort: the card
            // already exists, so a failed patch just leaves it on the
            // moment's day.
            const newPostId = data?.data?.id
            if (!options.contentScheduledAt || !newPostId) return data
            return updateContent({
              contentId: newPostId,
              params: { scheduled_at: options.contentScheduledAt },
              updateReduxEntity: true,
            }).then(
              () => data,
              () => data
            )
          })
      )
      const results = await Promise.allSettled(contentCreates)
      // Cleanup runs even if a content POST rejects — otherwise the
      // placeholder + disabled column stay stuck until page refresh.
      setPlaceholderMoment(null)
      setDisableActiveColumn(false)
      if (results.some((result) => result.status === 'fulfilled')) {
        refetchMoments()
      }
      const failure = results.find((result) => result.status === 'rejected')
      if (failure) throw failure.reason
    },
    [
      createMoment,
      story,
      createContent,
      loadUpdatedJsonApiEntities,
      queryClient,
      refetchMoments,
      updateContent,
      workspace_id,
    ]
  )

  // Content-only creation for the "attach to existing moment" flow in
  // `ContentQuickCreate`. Reuses the same post-create side effects
  // (deserialize response into Redux, invalidate in-market cache, toast)
  // as the create-new-moment path above so Content mode sees the new
  // card immediately — skipping `createMoment` since the parent already
  // exists.
  const createContentForMoment = React.useCallback(
    ({ momentId, service, name, scheduled_at, takedown_scheduled_at }) =>
      new Promise((resolve, reject) => {
        // Create-then-patch: the V2 content endpoint only whitelists
        // `name`, so we anchor the post to the user-clicked schedule via
        // a follow-up update. Both steps must succeed to report success.
        // `mutateAsync`, not `mutate`: a per-call `onSuccess` is dropped when
        // another create on this mutation starts first.
        createContent
          .mutateAsync({ serviceId: service.id, momentId, name })
          .then((data) => {
            invalidateInMarketQuery(queryClient, workspace_id)
            loadUpdatedJsonApiEntities?.(data)
            const newPostId = data?.data?.id

            const succeed = () => {
              refetchMoments()
              toast(<ContentCreatedToast selectedServices={[service]} />, {
                type: 'success',
                toastId: 'success-content-added-to-moment',
                closeOnClick: true,
              })
              resolve(data)
            }

            if (!scheduled_at || !newPostId) {
              succeed()
              return
            }

            updateContent({
              contentId: newPostId,
              params: takedown_scheduled_at
                ? { scheduled_at, takedown_scheduled_at }
                : { scheduled_at },
              updateReduxEntity: true,
            }).then(succeed, (error) => {
              console.error(
                'Attached content created, but schedule update failed',
                { error, newPostId, momentId }
              )
              // Content exists on the parent moment's schedule.
              // Surface a partial-success toast so the user knows to
              // re-pick the date manually.
              toast(
                'Content attached, but the date could not be set. Please update the schedule manually.',
                { type: 'warning' }
              )
              refetchMoments()
              resolve(data)
            })
          }, reject)
      }),
    [
      createContent,
      loadUpdatedJsonApiEntities,
      queryClient,
      refetchMoments,
      updateContent,
      workspace_id,
    ]
  )

  const onDeleteEntity = React.useCallback(
    ({ activeModalKey, entityType, params, withConfirmation }) => {
      if (entityType === 'moment' && withConfirmation === false) {
        return destroyMoment({ id: params.id })
      }
      return activateModalState({ activeModalKey, entityType, params })
    },
    [activateModalState, destroyMoment]
  )

  const deleteEntityOnConfirmation = React.useCallback(
    (entity) => {
      if (entity.type === 'moment') return destroyMoment(entity)
      return destroyContent(entity)
    },
    [destroyContent, destroyMoment]
  )

  const onUpdateMoment = React.useCallback(
    ({ momentId, update }, isUndoingMomentMove) => {
      const originalDate = moments[momentId].scheduled_at
      const movedFrom = startOfDay(parseISO(originalDate))
      const movedTo = startOfDay(parseISO(originalDate))
      const movedWithinSameWeek = isSameWeek(movedFrom, movedTo)

      let dropData = null
      const momentHasMoved =
        update.scheduled_at && originalDate !== update.scheduled_at

      if (!isUndoingMomentMove && momentHasMoved && !movedWithinSameWeek) {
        dropData = {
          day_segment: state.moments[momentId].day_segment,
          movedFrom: originalDate,
          movedTo: update.scheduled_at,
          id: momentId,
          isActive: true,
        }
      }
      setDisableActiveColumn(false)
      dispatch({
        type: actionTypes.UPDATE_MOMENT,
        data: {
          momentId,
          preConstraint,
          update,
          collapseContent,
          selectedSort,
          coverImageDisplay,
          workflowOnMomentsEnabled,
        },
      })

      updateMoment({ momentId, update })
        .then(() =>
          dispatch({ type: actionTypes.REMOVE_MOMENT_UPDATE, data: dropData })
        )
        .catch((err) => {
          throw err
        })
    },
    [
      moments,
      preConstraint,
      state.moments,
      selectedSort,
      updateMoment,
      workflowOnMomentsEnabled,
      collapseContent,
      coverImageDisplay,
    ]
  )

  const onAddMomentCreator = React.useCallback(
    (category, date) => {
      setPlaceholderMoment({
        brandId: currentBrandId.toString(),
        day_segment: category.id,
        height: 74,
        id: Number.MAX_SAFE_INTEGER,
        isPlaceholder: true,
        postCount: 0,
        primary_story: story,
        scheduled_at_raw: format(date, 'yyyy-MM-dd'),
        scheduled_at: date.toISOString(),
        title: '',
      })
    },
    [currentBrandId, story]
  )

  const onCancelMomentCreator = React.useCallback(
    () => setPlaceholderMoment(null),
    [setPlaceholderMoment]
  )

  const onRescheduleContent = React.useCallback(
    ({ contentId, scheduled_at, takedown_scheduled_at }) => {
      // Prevent-drop validation happened upstream. `takedown_scheduled_at`
      // is only sent in span-mode drags; PostUpdater converts it into a
      // `takedown_interval` server-side.
      const params = { scheduled_at }
      if (takedown_scheduled_at) {
        params.takedown_scheduled_at = takedown_scheduled_at
      }
      updateContent({
        contentId,
        params,
        updateReduxEntity: true,
      })
    },
    [updateContent]
  )

  const contextMenuCallbacks = React.useMemo(
    () =>
      buildContextMenuCallbacks({
        activateModalState,
        duplicateContent,
        launchMomentDuplicator,
        onAddToBoard,
        onAddToPlan,
        onDeleteEntity,
        onUpdateMoment,
        updateContent,
        queryClient,
        workspace_id,
      }),
    [
      activateModalState,
      duplicateContent,
      launchMomentDuplicator,
      onAddToBoard,
      onAddToPlan,
      onDeleteEntity,
      onUpdateMoment,
      updateContent,
      queryClient,
      workspace_id,
    ]
  )

  /**
   * This will appoximate grouping for testing, for now values are hard coded.
   * In the future we will need to pull category creation logic out of the Reducer
   * and into something that is better encapsulated and more consistent.
   *
   * Category rows are driven by the moment-level `currentWeekChannels` /
   * `currentWeekLabelSets` selectors; in Content mode we then filter down
   * to just the categories that actually have content in the visible week
   * so the grid doesn't surface empty rows that exist on a parent moment
   * but have no content in them.
   */
  const selectedSortCategories = React.useMemo(
    () =>
      buildCategoriesForSort({
        selectedSort,
        viewMode,
        content,
        filteredContent,
        moments,
        currentWeekChannels,
        currentWeekLabelSets,
        startDate: state.startDate,
        spanInMarketDays,
      }),
    [
      content,
      currentWeekChannels,
      currentWeekLabelSets,
      filteredContent,
      moments,
      selectedSort,
      spanInMarketDays,
      state.startDate,
      viewMode,
    ]
  )

  React.useEffect(() => {
    // Moment creation is only supported in Time-of-Day sort (categories
    // map to day_segment). Non-Time sorts disable the active-column state
    // so the CreateCard doesn't render in nonsensical categories.
    //
    // Content mode has its own creator (ContentQuickCreate) that works
    // across all sorts — so don't disable when we're in Content mode.
    const isContentMode = viewMode === WeekViewMode.Content
    if (!isContentMode && selectedSort.type !== WeekViewSortType.Time)
      return setDisableActiveColumn(true)
    setDisableActiveColumn(false)
  }, [selectedSort, setDisableActiveColumn, viewMode])

  // Content mode uses ContentQuickCreate, not the moment placeholder card, so
  // an open moment creator would otherwise linger and block content creation
  // after switching modes.
  React.useEffect(() => {
    setPlaceholderMoment(null)
  }, [viewMode, setPlaceholderMoment])

  const onSaveCoverAsset = React.useCallback(
    ({ moment, selectedAssetId, selectedAssetUrl }) => {
      onUpdateMoment({
        momentId: moment.id,
        update: {
          primary_asset_reference_id: selectedAssetId,
          previewUrl: selectedAssetUrl,
        },
      })
      deactivateModalState()
    },
    [deactivateModalState, onUpdateMoment]
  )

  const userNotMemberOfWorkspace =
    currentError &&
    currentError.error.response &&
    (currentError.error.response.status === 401 ||
      currentError.error.response.status === 403)

  const globalProviderValue = React.useMemo(
    () => ({
      activeColumn,
      contextMenuCallbacks,
      currentBrandId,
      disableMomentCreator:
        placeholderMoment !== null || (userRoleData?.isRestrictedUser ?? true),
      // Moment creator is disabled when sort isn't Time (categories don't
      // map to day_segment). Content creator works across all sorts, so
      // Content mode skips the sort-type gate and only uses the state var.
      disableActiveColumn:
        viewMode === WeekViewMode.Content
          ? disableActiveColumn
          : selectedSort.type !== WeekViewSortType.Time || disableActiveColumn,
      isLimitedUser: userRoleData?.isRestrictedUser ?? true,
      isLoadingContent: contentLoadsInFlight > 0,
      isLoadingMoments: state.isLoadingMoments,
      isMomentCreatorActive: placeholderMoment !== null,
      loadUpdatedJsonApiEntities,
      onAddMomentCreator,
      placeholderMoment,
      refetchMoments,
      setActiveColumn,
      setDisableActiveColumn,
      setPlaceholderMoment,
      story,
      weekViewStartDate: state.startDate,
    }),
    [
      activeColumn,
      contentLoadsInFlight,
      contextMenuCallbacks,
      currentBrandId,
      disableActiveColumn,
      loadUpdatedJsonApiEntities,
      onAddMomentCreator,
      placeholderMoment,
      refetchMoments,
      userRoleData,
      selectedSort,
      state.isLoadingMoments,
      state.startDate,
      story,
      viewMode,
    ]
  )

  const cardProviderValue = React.useMemo(
    () => ({
      cancelMoment: onCancelMomentCreator,
      createMoment: onCreateMoment,
      createContentForMoment,
      contextMenuCallbacks,
      errors,
      isDraggingContent,
      navigateTo,
      onDismissError,
      onMoveContent,
      onReorderContent,
      onRescheduleContent,
      requestAccess: requestPrivacyGroupAccess,
      setDragOrigin,
      setIsDraggingContent,
      story,
    }),
    [
      contextMenuCallbacks,
      createContentForMoment,
      errors,
      isDraggingContent,
      navigateTo,
      onCancelMomentCreator,
      onCreateMoment,
      onDismissError,
      onMoveContent,
      onReorderContent,
      onRescheduleContent,
      requestPrivacyGroupAccess,
      setDragOrigin,
      setIsDraggingContent,
      story,
    ]
  )

  return (
    <WeekViewGlobalContext.Provider value={globalProviderValue}>
      <MomentMapProvider
        interval={viewInterval}
        moments={state.moments}
        groupBy={selectedSort.type}
        parentCategory={
          selectedSort.type === WeekViewSortType.LabelSet
            ? selectedSort.title
            : undefined
        }
      >
        <ContentMapProvider
          interval={viewInterval}
          // Content mode consumes the content-first filtered slice so
          // channel/account/label/status filters target each post
          // individually. Moment mode renders posts nested under moment
          // cards via `moment.posts[]` and doesn't read this provider.
          content={visibleContent}
          moments={state.moments}
          groupBy={selectedSort.type}
          // Label-set id (not title): `getContentLabels` matches by id so two
          // sets sharing a display name aren't conflated and placement agrees
          // with `buildLabelSetCategories`.
          parentCategory={
            selectedSort.type === WeekViewSortType.LabelSet
              ? String(selectedSort.id)
              : undefined
          }
          spanInMarketDays={
            viewMode === WeekViewMode.Content && spanInMarketDays
          }
        >
          <CardContext.Provider value={cardProviderValue}>
            <div
              className={kuiperClassNames('weekView', {
                isScrolled: momentsAreScrolled,
              })}
              style={{
                '--weekView-scroll-offset': `${WEEK_VIEW_SCROLL_OFFSET}px`,
              }}
              ref={weekViewRef}
            >
              <ErrorBoundary
                errorComponent={<ErrorDisplay />}
                handleError={handleError}
              >
                {!userNotMemberOfWorkspace && (
                  <React.Fragment>
                    <DayHeaders
                      onUpdateMoment={onUpdateMoment}
                      start={state.startDate}
                      {...{
                        theme,
                      }}
                    />

                    <div className={kuiperClassNames('weekViewDayRows')}>
                      {!state.isLoadingMoments && !state.isLoadingLabelSets && (
                        <CategoryRows
                          activeFilters={activeFilters}
                          selectedFilters={selectedFilters}
                          categories={selectedSortCategories}
                          setMomentsAreScrolled={setMomentsAreScrolled}
                          targetDate={state.startDate}
                          {...{
                            theme,
                          }}
                        />
                      )}
                      <Loader
                        loaded={
                          !state.isLoadingMoments && !state.isLoadingLabelSets
                        }
                        theme="white"
                      />
                    </div>

                    <ActiveModal
                      activeModalKey={state.activeModalKey}
                      classes={['week-view-modal']}
                      deactivateModalState={deactivateModalState}
                      entity={state.activeEntityInModal}
                      isModalActive={!!state.activeEntityInModal}
                      renderOptions={modals({
                        deleteEntity: deleteEntityOnConfirmation,
                        onSaveAsset: onSaveCoverAsset,
                        uploadAsset,
                      })}
                      {...{
                        theme,
                      }}
                    />
                  </React.Fragment>
                )}
              </ErrorBoundary>
              {userNotMemberOfWorkspace && (
                <ErrorDisplay
                  {...{
                    currentError,
                    joinBrand,
                    theme,
                  }}
                />
              )}
            </div>
          </CardContext.Provider>
        </ContentMapProvider>
      </MomentMapProvider>
    </WeekViewGlobalContext.Provider>
  )
}
WeekView.displayName = 'Continuum: WeekView'

WeekView.propTypes = {
  activeFilters: PropTypes.array,
  selectedFilters: PropTypes.object,
  addViewToStore: PropTypes.func,
  content: PropTypes.object,
  createMoment: PropTypes.func,
  currentBrandId: PropTypes.number,
  currentError: PropTypes.object,
  currentWeekChannels: PropTypes.array,
  currentWeekLabelSets: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  deleteContent: PropTypes.func,
  deleteMoment: PropTypes.func,
  errors: PropTypes.array,
  focusedDay: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.number,
  ]),
  getContent: PropTypes.func,
  getContentById: PropTypes.func,
  getLabelSets: PropTypes.func,
  getMoments: PropTypes.func,
  handleError: PropTypes.func,
  isShroudVisible: PropTypes.bool,
  joinBrand: PropTypes.func,
  labelSets: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  launchMomentDuplicator: PropTypes.func,
  loadUpdatedJsonApiEntities: PropTypes.func.isRequired,
  momentContentIds: PropTypes.arrayOf(PropTypes.string),
  moments: PropTypes.object,
  navigateTo: PropTypes.func,
  onAddToBoard: PropTypes.func,
  onAddToPlan: PropTypes.func,
  onDayChange: PropTypes.func,
  onDismissError: PropTypes.func,
  preConstraint: PropTypes.object,
  removeJsonApiEntityRelationship: PropTypes.func.isRequired,
  removeViewFromStore: PropTypes.func,
  requestPrivacyGroupAccess: PropTypes.func,
  searchTerm: PropTypes.string,
  setSearchTerm: PropTypes.func,
  story: PropTypes.object,
  theme: PropTypes.oneOf(['dark', 'light']),
  trackers: PropTypes.object,
  updateContent: PropTypes.func,
  updateMoment: PropTypes.func,
  uploadAsset: PropTypes.func,
  weekStartsOn: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
}

export { WeekView }
export default getTrackableComponent(WeekView, 'WeekView')
function invalidateInMarketQuery(queryClient, workspace_id) {
  queryClient.invalidateQueries(queryKeys.inMarketContent({ workspace_id }))
}
